<template>
    <div
        :style="{ width: screenWidth + 'px' }"
        class="bg-black overflow-hidden w-full h-screen"
    >
        <!-- Left Status Border -->
        <status-indicator-border
            v-if="!multi || side === 'left'"
            :occupation-status="occupationIndicator"
            side="left"
        />

        <!-- Main Content Area -->
        <div class="content px-32" :class="{ 'flex justify-evenly': !multi }">
            <!-- Left Column -->
            <div class="left-column" :class="{ halfWidthClass }">
                <!-- Room Name -->
                <room-name-display
                    :room-name="item.name"
                    :max-width="halfScreenWidth"
                />

                <div :style="conditionalWidthClass">
                    <!-- Network Error Warning -->
                    <div class="mt-20">
                        <div
                            v-show="networkError"
                            class="text-white flex items-center"
                        >
                            <i
                                class="fa fa-exclamation-triangle"
                                aria-hidden="true"
                                style="color: red"
                            ></i>
                            <div class="ml-2">
                                Es besteht ein Problem mit der Internetverbindung
                            </div>
                        </div>
                    </div>

                    <!-- Room Status Display -->
                    <room-status-display
                        :timestamp="timestamp"
                        :occupation-status="occupationIndicator"
                        :next-booking="nextBooking"
                    />

                    <!-- Booking Actions -->
                    <room-booking-actions
                        :occupation-status="occupationIndicator"
                        :next-booking="nextBooking"
                        :loading="loading"
                        :network-error="networkError"
                        @book-now="createBooking"
                        @check-in="checkInBooking"
                        @release-room="releaseRoom"
                    />

                    <!-- Logo -->
                    <div class="fixed bottom-10">
                        <img
                            class="h-16"
                            src="/files/officeefficient_grau_rgb.png"
                            alt="logo"
                        />
                    </div>
                </div>
            </div>

            <!-- Divider -->
            <div
                v-if="!multi"
                :class="{ halfWidthClass }"
                class="devider h-80 w-1 bg-white rounded-xl ml-20 mr-20 mt-60"
            ></div>

            <!-- Right Column - Booking List -->
            <booking-list
                v-if="!multi"
                :bookings="occupation"
            />
        </div>

        <!-- Right Status Border -->
        <status-indicator-border
            v-if="!multi || side === 'right'"
            :occupation-status="occupationIndicator"
            side="right"
        />

        <portal-target name="modal"/>
    </div>
</template>

<script>
import sortBy from "lodash/sortBy";
import StatusIndicatorBorder from "../components/ConferenceRoom/StatusIndicatorBorder.vue";
import RoomNameDisplay from "../components/ConferenceRoom/RoomNameDisplay.vue";
import RoomStatusDisplay from "../components/ConferenceRoom/RoomStatusDisplay.vue";
import RoomBookingActions from "../components/ConferenceRoom/RoomBookingActions.vue";
import BookingList from "../components/ConferenceRoom/BookingList.vue";
import {
    calculateBookingEndTime,
    getCurrentDate,
    getCurrentTimeString,
    getHourFromTimestamp,
} from "../utils/conferenceRoomTime";
import {calculateOccupationStatus, findNextBooking, generateOccupationSlots,} from "../utils/conferenceRoomOccupation";

const REFRESH_INTERVAL_MS = 60000; // 1 minute
const TIME_UPDATE_INTERVAL_MS = 1000; // 1 second

export default {
    name: "ConferenceRoomScreenV2Refactored",

    components: {
        StatusIndicatorBorder,
        RoomNameDisplay,
        RoomStatusDisplay,
        RoomBookingActions,
        BookingList,
    },

    props: {
        id: {
            type: [String, Number],
            required: true,
        },
        multi: {
            type: Boolean,
            default: false,
        },
        side: {
            type: String,
            default: null,
            validator: (value) => value === null || ['left', 'middle', 'right'].includes(value),
        },
    },

    data: () => ({
        timestamp: "",
        item: {
            name: "",
        },
        bookings: [],
        hour: 0,
        networkError: false,
        loading: false,
        screenWidth: window.innerWidth,
        timeUpdateInterval: null,
        dataRefreshInterval: null,
    }),

    computed: {
        halfScreenWidth() {
            return window.innerWidth / 2;
        },

        halfWidthClass() {
            return `max-w-[${this.halfScreenWidth}px]`;
        },

        conditionalWidthClass() {
            return this.multi
                ? {maxWidth: `${this.halfScreenWidth}px`}
                : {width: `${this.halfScreenWidth}px`};
        },

        nextBooking() {
            return findNextBooking(this.bookings, this.timestamp);
        },

        occupationIndicator() {
            return calculateOccupationStatus(this.nextBooking, this.timestamp);
        },

        occupation() {
            return generateOccupationSlots(
                this.bookings,
                this.nextBooking,
                this.timestamp
            );
        },

        bookingEndTime() {
            return calculateBookingEndTime(
                this.nextBooking,
                this.occupationIndicator
            );
        },
    },

    mounted() {
        this.fetchRoomData();
        this.startIntervals();
    },

    beforeDestroy() {
        this.clearIntervals();
    },

    methods: {
        startIntervals() {
            this.updateTimestamp();
            this.timeUpdateInterval = setInterval(
                this.updateTimestamp,
                TIME_UPDATE_INTERVAL_MS
            );
            this.dataRefreshInterval = setInterval(
                this.fetchRoomData,
                REFRESH_INTERVAL_MS
            );
        },

        clearIntervals() {
            if (this.timeUpdateInterval) {
                clearInterval(this.timeUpdateInterval);
            }
            if (this.dataRefreshInterval) {
                clearInterval(this.dataRefreshInterval);
            }
        },

        updateTimestamp() {
            this.timestamp = getCurrentTimeString();
            const currentHour = getHourFromTimestamp(this.timestamp);
            if (currentHour !== this.hour) {
                this.hour = currentHour;
            }
        },

        fetchRoomData() {
            window.axios
                .get(`/panel/conference_rooms/${this.id}`)
                .then((response) => {
                    this.item = response.data.data;
                    this.bookings = sortBy(response.data.data.bookings, "start");
                    this.loading = false;
                    this.networkError = false;
                })
                .catch((error) => {
                    this.handleError(error);
                });
        },

        createBooking() {
            this.loading = true;
            const [hour, minute] = this.timestamp.slice(0, 5).split(":");
            const today = getCurrentDate();

            window.axios
                .post("/panel/conference_rooms", {
                    start: `${today} ${hour}:${minute}:00`,
                    end: this.bookingEndTime,
                    comment: "Displaybuchung",
                    conference_room_id: this.id,
                })
                .then(() => {
                    this.fetchRoomData();
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleError(error);
                });
        },

        checkInBooking() {
            this.loading = true;
            if (!this.nextBooking) {
                this.loading = false;
                return;
            }

            window.axios
                .put(`/panel/conference_room_bookings/${this.nextBooking.id}/check-in`, {
                    uuid: this.id,
                })
                .then(() => {
                    this.fetchRoomData();
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleError(error);
                });
        },

        releaseRoom() {
            this.loading = true;
            if (!this.nextBooking) {
                this.loading = false;
                return;
            }

            window.axios
                .put(`/panel/conference_room_bookings/${this.nextBooking.id}/release`, {
                    uuid: this.id,
                })
                .then(() => {
                    this.fetchRoomData();
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleError(error);
                });
        },

        handleError(error) {
            if (error.message === "Network Error") {
                this.networkError = true;
            } else {
                this.$root.$emit("error", error);
            }
        },
    },
};
</script>

<style scoped>
/* Background Colors */
.bg-black {
    background-color: #000000;
}
.bg-white {
    background-color: #ffffff;
}

/* Text Colors */
.text-white {
    color: #ffffff;
}

/* Layout */
.overflow-hidden {
    overflow: hidden;
}

/* Sizing */
.w-full {
    width: 100%;
}
.w-1 {
    width: 0.25rem;
}
.h-screen {
    height: 100vh;
}
.h-16 {
    height: 4rem;
}
.h-80 {
    height: 20rem;
}

/* Flexbox */
.flex {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
}
.justify-evenly {
    -webkit-box-pack: space-evenly;
    -ms-flex-pack: space-evenly;
    justify-content: space-evenly;
}
.items-center {
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
}

/* Positioning */
.absolute {
    position: absolute;
}

.fixed {
    position: fixed;
}

.bottom-10 {
    bottom: 2.5rem;
}

/* Spacing - Padding */
.px-32 {
    padding-left: 8rem;
    padding-right: 8rem;
}

/* Spacing - Margin */
.mt-20 {
    margin-top: 5rem;
}
.mt-60 {
    margin-top: 15rem;
}
.ml-2 {
    margin-left: 0.5rem;
}
.ml-20 {
    margin-left: 5rem;
}
.mr-20 {
    margin-right: 5rem;
}

/* Border Radius */
.rounded-xl {
    -webkit-border-radius: 0.75rem;
    -moz-border-radius: 0.75rem;
    border-radius: 0.75rem;
}

/* Content wrapper */
.content {
    position: relative;
    height: 100%;
}

/* Left column container */
.left-column {
    position: relative;
}

/* Divider element */
.devider {
    -ms-flex-negative: 0;
    flex-shrink: 0;
}
</style>
